import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  ListItemText,
} from '@mui/material';
import {
  SearchRounded,
  NotificationsNoneRounded,
  FactCheck,
  AutoAwesome,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService, approvalService, NotificationItem } from '../../services/workspaceService';
import AssistantPanel, { useAssistantAvailable } from '../../ai/AssistantPanel';
import AppsGridMenu from '../shell/AppsGridMenu';
import { useAppTheme } from '../../theme';

const SEVERITY_ORDER = ['CRITICAL', 'IMPORTANT', 'NORMAL', 'INFO'];

interface HeaderActionsProps {
  /**
   * Opens the command palette. Ownership of the palette moved UP to Header in
   * the shell redesign so the wide command bar (centre of the top bar) and this
   * icon-only fallback (below `md`) drive one dialog and one Ctrl+K listener —
   * mounting `useCommandPalette` twice would register two global key handlers
   * that toggle each other back closed.
   */
  onOpenPalette: () => void;
}

/**
 * Top-bar action cluster: module grid, approvals queue, notifications, assistant.
 *
 * Kept out of Header.tsx so the header stays a layout component and this stays
 * the place that talks to the workspace APIs.
 */
const HeaderActions: React.FC<HeaderActionsProps> = ({ onOpenPalette }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { colors } = useAppTheme();
  const [assistantOpen, setAssistantOpen] = React.useState(false);
  const [notifAnchor, setNotifAnchor] = React.useState<null | HTMLElement>(null);
  const assistantAvailable = useAssistantAvailable();

  // Polled rather than pushed — there is no WebSocket infrastructure, and a
  // 60s poll on two small counts is honest about that rather than pretending
  // to be realtime.
  const { data: unread } = useQuery<{ total: number; bySeverity: Record<string, number> }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.unreadCount(),
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: approvals } = useQuery<{ summary: Array<{ entity_type: string; count: number }>; total: number }>({
    queryKey: ['approvals', 'summary'],
    queryFn: () => approvalService.summary(),
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: notifList } = useQuery<any>({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationService.list({ unreadOnly: true, limit: 8 }),
    enabled: Boolean(notifAnchor),
    retry: false,
  });

  const markAll = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications: NotificationItem[] = (notifList as any)?.notifications ?? [];
  const unreadTotal = unread?.total ?? 0;
  const approvalTotal = approvals?.total ?? 0;

  const sorted = [...notifications].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
  );

  // The top bar is a light surface now, so icon buttons take the workspace's
  // secondary text colour rather than `inherit` (which was white-on-navy).
  const iconSx = {
    color: colors.textSecondary,
    '&:hover': { backgroundColor: colors.background, color: colors.textPrimary },
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {/* Icon-only fallback below md, where the wide command bar is hidden. */}
        <Tooltip title="Search  ·  Ctrl+K">
          <IconButton
            onClick={onOpenPalette}
            aria-label="open command palette"
            sx={{ ...iconSx, display: { xs: 'inline-flex', md: 'none' } }}
          >
            <SearchRounded fontSize="small" />
          </IconButton>
        </Tooltip>

        <AppsGridMenu />

        {approvalTotal > 0 && (
          <Tooltip title={`${approvalTotal} awaiting your approval`}>
            <IconButton onClick={() => navigate('/approvals/pending')} aria-label="approval queue" sx={iconSx}>
              <Badge badgeContent={approvalTotal} color="warning" max={99}>
                <FactCheck fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Notifications">
          <IconButton
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            aria-label={`notifications${unreadTotal ? `, ${unreadTotal} unread` : ''}`}
            aria-haspopup="menu"
            sx={iconSx}
          >
            <Badge badgeContent={unreadTotal} color="error" max={99}>
              <NotificationsNoneRounded fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* No AI credentials -> no entry point at all. A button that always
            errors is worse than no button. */}
        {assistantAvailable && (
          <Tooltip title="Stratify AI">
            <IconButton onClick={() => setAssistantOpen(true)} aria-label="open assistant" sx={iconSx}>
              <AutoAwesome fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{ sx: { width: 360, maxHeight: 460, borderRadius: 2, mt: 1 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Notifications
          </Typography>
          {unreadTotal > 0 && (
            <Tooltip title="Mark all read">
              <IconButton size="small" onClick={() => markAll.mutate()}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Divider />

        {sorted.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              You're all caught up
            </Typography>
          </Box>
        )}

        {sorted.map((n) => (
          <MenuItem
            key={n.id}
            onClick={() => {
              if (n.link) navigate(n.link);
              setNotifAnchor(null);
            }}
            sx={{ py: 1.25, alignItems: 'flex-start', whiteSpace: 'normal' }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {/* Severity is a labelled chip, never a bare colour. */}
                  {(n.severity === 'CRITICAL' || n.severity === 'IMPORTANT') && (
                    <Chip
                      label={n.severity === 'CRITICAL' ? 'Critical' : 'Important'}
                      size="small"
                      color={n.severity === 'CRITICAL' ? 'error' : 'warning'}
                      sx={{ height: 17, fontSize: '0.625rem' }}
                    />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {n.title}
                    {n.count && n.count > 1 ? ` ×${n.count}` : ''}
                  </Typography>
                </Box>
              }
              secondary={n.body}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>

      {assistantAvailable && (
        <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      )}
    </>
  );
};

export default HeaderActions;
